import { Component, Input, Output, EventEmitter, SimpleChanges, OnChanges, OnDestroy } from '@angular/core';
import { Subscription, Subject } from 'rxjs';
import { SapiService } from '../../../services/sapi.service';
import { BbSelectOption } from '../../shared/bb-select/bb-select.component';
import { TranslateService } from '../../../../node_modules/@ngx-translate/core';
import { BbTableService, TableRow, TableSort } from '../../../services/bb-table.service';
import { debounceTime } from 'rxjs/operators';
import { Social } from './social-details.component';
import { Playlist } from '../../../interfaces/playlist';
import { BbFilterConfiguration } from '../../shared/bb-filters/bb-filters.component';
import { NgForm } from '../../../../node_modules/@angular/forms';
import { FilterSet } from '../../../services/filter-set.service';
import { MediaclipService } from '../../../services/mediaclip.service';

@Component({
	selector: 'social-details-automated-syncing',
	templateUrl: './social-details-automated-syncing.component.html',
	styleUrls: ['./social-details-automated-syncing.component.scss']
})

export class SocialDetailsAutomatedSyncingComponent implements OnChanges, OnDestroy {
	@Input() bbTableService: BbTableService;
	@Input() form: NgForm;
	@Input() rows: TableRow[];

	@Output() entityChange = new EventEmitter();
	@Output() rowsChanged = new EventEmitter();
	@Output() queryStringChange: EventEmitter<boolean> = new EventEmitter();

	subscriptions: Subscription;

	filterConfig: BbFilterConfiguration;
	filterSort: TableSort;
	filterLimit: number;

	sortPropertyOptions: BbSelectOption[];

	cliplist: string;

	_entity: Social;

	limitChanged: Subject<number>;
	filterChanged: Subject<string>;
	queryChanged: Subject<string>;

	baseFilterSet: FilterSet;

	@Input()
	get entity(): Social {
		return this._entity;
	}

	set entity(entity) {
		this._entity = entity;
		this.entityChange.emit(this._entity);
	}


	constructor(
		private sapiService: SapiService,
		private mediaclipService: MediaclipService,
		private translate: TranslateService
	) {
		this.sortPropertyOptions = [
			{ value: 'score', label: this.translate.instant('SCORE') },
			{ value: 'publisheddate', label: this.translate.instant('PUBLISHED_DATE') },
			{ value: 'createddate', label: this.translate.instant('CREATED_DATE') },
			{ value: 'updateddate', label: this.translate.instant('UPDATED_DATE') },
			{ value: 'title', label: this.translate.instant('TITLE') },
			{ value: 'sourcetype', label: this.translate.instant('SOURCETYPE') },
			{ value: 'YouTube_status', label: this.translate.instant('STATUS') }
		];

		this.subscriptions = new Subscription();

		this.limitChanged = new Subject<number>();
		this.filterChanged = new Subject<string>();
		this.queryChanged = new Subject<string>();

		this.subscriptions.add(this.limitChanged.pipe(
			debounceTime(400)
		).subscribe((limit) => {
			this.onQueryLimitChange(limit);
		}));

		this.subscriptions.add(this.filterChanged.pipe(
			debounceTime(400)
		).subscribe((filterSet) => {
			this.onFilterChange(filterSet);
		}));

		this.subscriptions.add(this.queryChanged.pipe(
			debounceTime(400)
		).subscribe((query) => {
			this.onQueryChange(query);
		}));

		this.filterConfig = {
			search: true,
			filters: this.mediaclipService.getFilterConfigOptions(),
		};

		this.baseFilterSet = [
			{
				filters: [{
					type: 'mediaclip',
					field: 'type',
					operator: 'is',
					value: 'MediaClip'
				}]
			},
			{
				filters: [{
					type: 'mediaclip',
					field: 'mediatype',
					operator: 'is',
					value: 'video'
				}],
			},
			{
				filters: [{
					type: 'mediaclip',
					field: 'sourcetype',
					operator: 'is',
					value: 'on_demand'
				}],
			},
			{
				filters: [{
					type: 'mediaclip',
					field: 'status',
					operator: 'is',
					value: 'published'
				}]
			}
		];
	}

	init() {
		if (this.entity) {

			this.bbTableService.setBaseFilterSet(this.baseFilterSet);

			this.cliplist = this.entity.cliplistid;
			if (this.entity.cliplist && this.entity.cliplist.sort) {
				this.filterSort = this.createSortFromString(this.entity.cliplist.sort);
			}
			if (!this.entity.privacy) {
				this.entity.privacy = 'public';
			}

			if (this.entity.automatedSync) {
				this.triggerTableUpdate();
			} else {
				this.rows = [];
				this.rowsChanged.emit(this.rows);
			}
		}
	}

	createSortFromString(sort: string): TableSort {
		let filterSort: TableSort;
		if (typeof sort === 'undefined' || sort == null) {
			sort = 'publisheddate desc';
			filterSort = {
				sortProperty: 'publisheddate',
				sortDirection: 'desc'
			};
		} else {
			const parts = sort.split(' ');
			if (parts && parts.length === 1) {
				parts.push('desc');
			}

			filterSort = {
				sortProperty: parts[0],
				sortDirection: parts[1]
			};
		}
		return filterSort;
	}

	ngOnDestroy() {
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}

	ngOnChanges(simpleChanges: SimpleChanges) {
		if (simpleChanges) {
			if (simpleChanges.entity && simpleChanges.entity.currentValue) {
				this.init();
			}
			if (simpleChanges.cliplist) { }
		}
	}

	triggerTableUpdate() {
		switch (this.entity.filterType) {
			case 'filters':
				this.onFilterChange(this.entity.cliplist.filters);
				break;
			case 'solr':
				this.onQueryChange(this.entity.cliplist.query);
				break;
			case 'cliplist':
				this.onSelectedCliplist();
				break;
		}
	}

	onSelectedCliplist(cliplistId?: string) {
		if (cliplistId || this.cliplist) {
			if (this.entity.cliplistid !== this.cliplist) {
				this.form.form.markAsDirty();
			}
			if (!cliplistId) {
				cliplistId = this.cliplist;
			}
			if (!this.cliplist) {
				this.cliplist = cliplistId;
			}
			this.entity.cliplistid = this.cliplist;
			this.sapiService.get('cliplist', cliplistId).subscribe((playlist: Playlist) => {
				if (playlist) {
					if (playlist.listtype === 'static' && playlist.items) {
						const rows = playlist.items.map((mediaclip) => ({
							id: mediaclip.id,
							data: mediaclip
						}));
						this.rows = rows;
						this.rowsChanged.emit(this.rows);
					} else if (playlist.listtype === 'dynamic') {
						this.filterSort = this.createSortFromString(playlist.sort);
						this.filterLimit = playlist.limit;
						if (playlist.filterType === 'filters') {
							this.onFilterChange(playlist.filters, false);
						} else {
							this.onQueryChange(playlist.query, true, false);
						}
					}
				}
			});
			this.entityChange.emit(this._entity);
		} else {
			this.rows = [];
			this.rowsChanged.emit(this.rows);
		}
	}

	onUseLimitChange(val: Event) {
		if (val && this.entity.cliplist.limit !== 30) {
			this.entity.cliplist.limit = 30;
		} else if (!val && this.entity.cliplist.limit > -1) {
			this.entity.cliplist.limit = -1;
		}
		this.bbTableService
			.setMaxTotalAmount(this.entity.cliplist.limit)
			.getRows(true);
	}

	onQueryLimitChange(limit: number) {
		this.entity.cliplist.limit = limit;
		this.bbTableService
			.setMaxTotalAmount(this.entity.cliplist.limit)
			.getRows(true);
	}

	onSortChange() {
		this.entity.cliplist.sort = this.filterSort.sortProperty + ' ' + this.filterSort.sortDirection;
		this.bbTableService
			.setSort(this.filterSort)
			.updateSort(this.filterSort.sortProperty, true)
			.getRows(true);
	}

	onFilterChange(filters: any, changeEntity = true) {
		if (this.entity.automatedSync) {
			if (changeEntity) {
				this.entity.cliplist.filters = filters;
				this.filterSort = this.createSortFromString(this.entity.cliplist.sort);
				this.filterLimit = this.entity.cliplist.limit;
			}
			this.bbTableService
				.setFilterSet(filters)
				.setBaseFilterSet(this.baseFilterSet)
				.setMaxTotalAmount(this.filterLimit)
				.setSort(this.filterSort)
				.updateSort(this.filterSort.sortProperty, true)
				.setFilterQuery('')
				.useOverlayLoading(false)
				.getRows(true);
		}
		this.queryStringChange.emit(false);
	}

	onQueryChange(query: string, force = false, changeEntity = true) {
		if (force || this.entity.cliplist.query !== query) {
			if (changeEntity) {
				this.entity.cliplist.filters = null;
				this.entity.cliplist.query = query;
				this.filterSort = this.createSortFromString(this.entity.cliplist.sort);
				this.filterLimit = this.entity.cliplist.limit;
			}
			this.bbTableService
				.setFilterSet([])
				.setBaseFilterSet(this.baseFilterSet)
				.setMaxTotalAmount(this.filterLimit)
				.setSort(this.filterSort)
				.updateSort(this.filterSort.sortProperty, true)
				.setFilterQuery(query)
				.useOverlayLoading(false)
				.getRows(true);
		}
		this.queryStringChange.emit(true);
	}

	onFilterClick(filterType: string) {
		this.entity.filterType = filterType;
		if (!this.entity.cliplist) {
			this.entity.cliplist = { usetype: 'social' };
		}
		this.entity.cliplist.filterType = filterType;
		if (filterType === 'filters') {
			this.cliplist = null;
			this.onFilterChange(this.entity.cliplist.filters || []);
		} else if (filterType === 'solr') {
			this.cliplist = null;
			if (!this.entity.cliplist.query) {
				this.entity.cliplist.query = '*:*';
			}
			if (!this.entity.cliplist.filters) {
				this.onQueryChange(this.entity.cliplist.query, true, false);
			}
		} else {
			this.onSelectedCliplist(this.entity.cliplistid);
		}
	}

	automatedSyncChanged(automatedSync: boolean) {
		if (automatedSync) {
			this.triggerTableUpdate();
		} else {
			this.rows = [];
			this.rowsChanged.emit(this.rows);
		}
		this.form.form.markAsDirty();
	}
}
